/**
 * Database Change Notifier Service
 * 
 * This service acts as a bridge between database operations and sync coordination.
 * It notifies the sync coordinator when database changes are made by user actions.
 *
 * Uses dynamic import for syncCoordinator to avoid circular dependency:
 * dbStorageService -> bulkOperationsHelper -> dbChangeNotifier -> syncCoordinator -> syncService -> SyncServiceImpl -> dbStorageService
 */

import { NOTIFICATION_EVENTS, createCustomEvent } from '../eventConstants';

class DbChangeNotifier {
    constructor() {
        this.changeListeners = new Set();
    }

    /**
     * Notify that a user-initiated database change has occurred
     * @param {string} tableName - The name of the table that was changed
     * @param {string|Array<Object>} data - The data that was changed or operation type
     * @param {string} operation - The type of operation (upsert, markForDeletion)
     * @param {boolean} isUserInitiated - Whether the change was initiated by a user action
     */
    notifyChange(tableName, data, operation, isUserInitiated = true) {
        // Handle legacy function signature
        if (typeof operation === 'object' && operation.isUserInitiated !== undefined) {
            isUserInitiated = operation.isUserInitiated;
            operation = data; // In this case, data is actually the operation
            data = null;
        }

        console.debug(`Database change: ${operation} in ${tableName}, user-initiated: ${isUserInitiated}`);

        if (isUserInitiated) {
            // Dynamic import to avoid circular dependency at load time
            import('../SyncCoordinator').then(({ default: getSyncCoordinator }) => {
                const syncCoordinator = getSyncCoordinator();
                syncCoordinator.handleUserActivity();
            }).catch((err) => {
                console.warn('[DbChangeNotifier] Could not notify sync coordinator:', err);
            });

            // Also dispatch an event for the sync coordinator to catch
            const detail = {
                tableName,
                operation,
                count: Array.isArray(data) ? data.length : (data?.count || 1)
            };
            const event = createCustomEvent(NOTIFICATION_EVENTS.LOCAL_CHANGE_OCCURRED, detail);
            window.dispatchEvent(event);
            // console.log(`[DbChangeNotifier] Dispatched ${NOTIFICATION_EVENTS.LOCAL_CHANGE_OCCURRED} event for ${operation} in ${tableName}`);
        }

        // Notify any other registered listeners
        this.changeListeners.forEach(listener => {
            try {
                listener(tableName, operation, { data, isUserInitiated });
            } catch (error) {
                console.error('Error in change listener:', error);
            }
        });
    }

    /**
     * Add a listener for database changes
     * @param {Function} listener - Function to call when changes occur
     */
    addChangeListener(listener) {
        if (typeof listener === 'function') {
            this.changeListeners.add(listener);
        }
    }

    /**
     * Remove a previously added listener
     * @param {Function} listener - The listener to remove
     */
    removeChangeListener(listener) {
        this.changeListeners.delete(listener);
    }
}

// Create a singleton instance
const dbChangeNotifier = new DbChangeNotifier();
export default dbChangeNotifier; 